import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getAuth } from 'firebase/auth';
import { useEcosystemSolver } from '../../hooks/useEcosystemSolver';
import Results from './Results';
import { motion as motionDiv } from 'framer-motion';
import { DndContext, closestCenter, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// console.log('process.env:', process.env);

function OcrUpload({ onMultiCard }) {
  const [isDragging, setIsDragging] = useState(false);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState(new Map()); // Track blob URLs
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [step, setStep] = useState(1); // 1: upload, 2: review, 3: results
  const [solverError, setSolverError] = useState('');
  const [editingChip, setEditingChip] = useState(null); // { cardIdx, field, chipIdx }
  const [isMobile, setIsMobile] = useState(false);
  const [totalProcessedImages, setTotalProcessedImages] = useState(0);
  const MAX_IMAGES = 13;
  const BATCH_SIZE = 3;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // console.log('✅ FINAL API URL:', process.env.REACT_APP_API_URL);
    }
  }, []);

  // Add mobile detection without changing desktop experience
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile(); // Initial check
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Notify parent of multi-card state
  useEffect(() => {
    const many = step === 2 && results.length > 1;
    onMultiCard?.(many);
  }, [step, results.length, onMultiCard]);

  // Add this flag to control layout
  const isMultiCard = step === 2 && results.length > 1;

  // Use the same hook as manual entry
  const { solve, result, loading } = useEcosystemSolver();

  // Move fieldMap here so it's available everywhere
  const fieldMap = {
    name: 'string',
    caloriesProvided: 'number',
    caloriesNeeded: 'number',
    temperatureRange: 'string',
    depthRange: 'string',
    foodSources: 'array',
    eatenBy: 'array',
  };

  // Cleanup blob URLs when component unmounts or images change
  useEffect(() => {
    return () => {
      imagePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    
    // Check if adding these files would exceed the total limit
    if (totalProcessedImages + files.length > MAX_IMAGES) {
      setError(`You can upload a maximum of ${MAX_IMAGES} images in total. You have already processed ${totalProcessedImages} images.`);
      return;
    }

    // Limit to batch size
    const batchFiles = files.slice(0, BATCH_SIZE);
    if (batchFiles.length > 0) {
      const newPreviews = new Map();
      batchFiles.forEach(file => {
        newPreviews.set(file, URL.createObjectURL(file));
      });
      setImagePreviews(newPreviews);
      setImages(batchFiles);
      setError('');
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    
    // Check if adding these files would exceed the total limit
    if (totalProcessedImages + files.length > MAX_IMAGES) {
      setError(`You can upload a maximum of ${MAX_IMAGES} images in total. You have already processed ${totalProcessedImages} images.`);
      return;
    }

    // Limit to batch size
    const batchFiles = files.slice(0, BATCH_SIZE);
    if (batchFiles.length > 0) {
      const newPreviews = new Map();
      batchFiles.forEach(file => {
        newPreviews.set(file, URL.createObjectURL(file));
      });
      setImagePreviews(newPreviews);
      setImages(batchFiles);
      setError('');
    }
  };

  const uploadToS3 = async (file) => {
    try {
      const auth = getAuth();
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Get presigned URL from Cloud Run backend
      const presignResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/s3/presign?filename=${encodeURIComponent(file.name)}&type=${encodeURIComponent(file.type)}&size=${encodeURIComponent(file.size)}`, {
        headers
      });
      if (!presignResponse.ok) {
        throw new Error('Failed to get upload URL');
      }
      const { url, key } = await presignResponse.json();

      // Upload to S3
      const uploadResponse = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to S3');
      }

      return key;
    } catch (error) {
      // console.error('S3 upload failed:', error);
      throw error;
    }
  };

  const processImages = async (files) => {
    setProcessing(true);
    setError('');
    try {
      // In development, use the local batch endpoint (Phase B: requires auth)
      if (process.env.NODE_ENV === 'development') {
        const formData = new FormData();
        files.forEach(file => {
          formData.append('images', file);
        });
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ocr/batch`, {
          method: 'POST',
          headers,
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Failed to process images: ${response.status}`);
        }

        const newResults = await response.json();
        setResults(prevResults => [...prevResults, ...(Array.isArray(newResults) ? newResults : [])]);
        setTotalProcessedImages(prev => prev + files.length);
        setStep(2);
        return;
      }

      // Production flow using S3
      const keys = [];
      for (let i = 0; i < files.length; i++) {
        keys.push(await uploadToS3(files[i]));
      }

      // Submit each image for OCR processing
      const auth = getAuth();
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const authHeaders = token
        ? {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        : { 'Content-Type': 'application/json' };
      const jobIds = [];
      for (let i = 0; i < keys.length; i++) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ocr/submit`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ key: keys[i] })
        });

        if (!response.ok) {
          throw new Error(`Failed to submit image for OCR: ${response.status}`);
        }
        
        const { jobId } = await response.json();
        jobIds.push(jobId);
      }

      // Poll for results with retry limits and timeout protection
      const pollResults = async (jobId, attempts = 0) => {
        const maxAttempts = 30; // 30 attempts * 2 seconds = 60 seconds max
        const retryDelay = 2000; // 2 seconds between attempts
        
        if (attempts >= maxAttempts) {
          throw new Error(`OCR processing timed out after ${maxAttempts * retryDelay / 1000} seconds`);
        }
        
        try {
          
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ocr/result?jobId=${jobId}`, {
            method: 'GET',
            headers: token
              ? {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`
                }
              : {
                  'Content-Type': 'application/json'
                }
          });
          
          if (response.status === 202) {
            // Job is still pending - continue polling
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            return pollResults(jobId, attempts + 1);
          }
          
          if (!response.ok) {
            if (response.status === 404) {
              // Job not found - this means it was never created or was deleted
              throw new Error('OCR job not found - it may have failed to be submitted or was deleted');
            }
            if (response.status === 500) {
              throw new Error('OCR endpoint error - check Cloud Run deployment');
            }
            throw new Error(`OCR request failed: ${response.status} ${response.statusText}`);
          }
          
          const data = await response.json();
          
          // Check for PENDING status in response body (legacy support)
          if (data.status === 'PENDING') {
            // console.log(`OCR still pending for job ${jobId}, attempt ${attempts + 1}/${maxAttempts}`);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            return pollResults(jobId, attempts + 1);
          }
          
          return data;
        } catch (error) {
          // console.error(`OCR polling error for job ${jobId}, attempt ${attempts + 1}:`, error);
          
          // If it's a network error or 404, don't retry
          if (error.message.includes('not found') || error.message.includes('failed to be submitted')) {
            throw error;
          }
          
          // If it's a network error, retry; otherwise throw
          if (error.name === 'TypeError' && error.message.includes('fetch')) {
            if (attempts < maxAttempts - 1) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              return pollResults(jobId, attempts + 1);
            }
          }
          
          throw error;
        }
      };

      const results = await Promise.all(jobIds.map(pollResults));
      setResults(prevResults => [...prevResults, ...(Array.isArray(results) ? results : [])]);
      setTotalProcessedImages(prev => prev + files.length);
      setStep(2);
    } catch (err) {
      setError(err.message || 'Failed to process images');
    } finally {
      setProcessing(false);
    }
  };

  // SolveEcosystemButton copied from ManualEntry.js
  const SolveEcosystemButton = ({ onSolve, isLoading, progress }) => {
    return (
      <motionDiv.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-8 flex justify-center"
      >
        <motionDiv.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onSolve}
          disabled={isLoading}
          className={`
            relative overflow-hidden
            px-8 py-4 rounded-xl
            bg-gradient-to-r from-blue-600/20 to-blue-800/20
            border border-blue-500/30
            backdrop-blur-sm
            ${isLoading ? 'cursor-wait' : 'cursor-pointer'}
            group
            w-full
          `}
        >
          {/* Knight Rider effect */}
          <motionDiv.div
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{
              repeat: Infinity,
              duration: 2,
              ease: "linear"
            }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent"
          />

          {/* Glow effect */}
          <motionDiv.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.5, 0] }}
            transition={{
              repeat: Infinity,
              duration: 2,
              ease: "easeInOut"
            }}
            className="absolute inset-0 bg-blue-500/10 blur-xl"
          />

          {/* Button content */}
          <div className="relative flex items-center justify-center gap-3 w-full">
            <span className="text-lg font-semibold text-blue-300">
              {isLoading ? 'Solving Ecosystem...' : 'Solve Ecosystem'}
            </span>
            {isLoading && (
              <motionDiv.div
                initial={{ width: 0 }}
                animate={{ width: '100px' }}
                className="h-2 bg-blue-500/30 rounded-full overflow-hidden"
              >
                <motionDiv.div
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{
                    repeat: Infinity,
                    duration: 1,
                    ease: "linear"
                  }}
                  className="h-full w-1/2 bg-blue-500"
                />
              </motionDiv.div>
            )}
          </div>

          {/* Progress indicator */}
          {isLoading && (
            <motionDiv.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute -bottom-6 left-0 right-0 text-center text-sm text-blue-400"
            >
              {progress}%
            </motionDiv.div>
          )}
        </motionDiv.button>
      </motionDiv.div>
    );
  };

  function EditableDraggableChip({ id, value, onEdit, listeners, attributes, isDragging, editing, setEditing }) {
    const inputRef = React.useRef();
    React.useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);
    const handleRemove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // id format: field-idx-chipIdx
      const parts = id.split('-');
      const chipIdx = Number(parts.pop());
      const cardIdx = Number(parts.pop());
      const field = parts.join('-');
      setResults(prevResults => {
        const newResults = [...prevResults];
        const chipsArr = toChips(newResults[cardIdx][field]);
        if (chipIdx >= 0 && chipIdx < chipsArr.length) {
          const newChipsArr = [...chipsArr];
          newChipsArr.splice(chipIdx, 1);
          newResults[cardIdx][field] = newChipsArr;
        }
        return newResults;
      });
    };
    return (
      <div
        className={`inline-flex items-center px-2 py-1 m-1 rounded bg-blue-100 text-blue-800 border border-blue-300 ${editing ? '' : 'cursor-default'} ${isDragging ? 'ring-2 ring-blue-400' : ''}`}
        tabIndex={0}
        title={editing ? 'Editing' : 'Drag ≡ or click ✏️ to edit'}
      >
        {/* Drag handle (only shows when not editing) */}
        {!editing && (
          <span
            {...attributes}
            {...listeners}
            className="mr-1 cursor-grab select-none text-blue-400 hover:text-blue-600 px-1"
            title="Drag to move"
            style={{ fontSize: 18, userSelect: 'none', display: 'flex', alignItems: 'center' }}
          >
            ≡
          </span>
        )}
        {editing ? (
          <input
            ref={inputRef}
            value={value}
            placeholder="Type value..."
            onChange={e => onEdit(e.target.value)}
            onBlur={() => setEditing(false)}
            onKeyDown={e => { if (e.key === 'Enter') setEditing(false); }}
            className="bg-transparent border-none focus:outline-none w-auto max-w-[120px] text-blue-800 font-medium"
          />
        ) : (
          <>
            <span
              className={value === '' ? 'opacity-50 italic' : ''}
              style={{ cursor: 'pointer', padding: '0 2px', userSelect: 'text', flex: 1, background: '#e0e7ff' }}
            >
              {value === '' ? 'Type value...' : value}
            </span>
            <button
              type="button"
              className="ml-1 text-blue-400 hover:text-blue-700 focus:outline-none text-xs font-bold px-1"
              onClick={e => { e.preventDefault(); e.stopPropagation(); setEditing(true); }}
              title="Edit"
              tabIndex={-1}
              style={{ display: 'flex', alignItems: 'center', minWidth: 22 }}
            >
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <title>Edit</title>
                <path d="M4 13.5V16h2.5l7.06-7.06-2.5-2.5L4 13.5z" fill="currentColor"/>
                <path d="M17.71 6.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
              </svg>
            </button>
          </>
        )}
        <button
          type="button"
          className="ml-1 text-blue-400 hover:text-red-500 focus:outline-none text-xs font-bold px-1"
          onClick={handleRemove}
          title="Remove"
          tabIndex={-1}
        >
          ×
        </button>
      </div>
    );
  }

  function SortableEditableChip(props) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.id });
    return (
      <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}>
        <EditableDraggableChip
          {...props}
          listeners={listeners}
          attributes={attributes}
          isDragging={isDragging}
        />
      </div>
    );
  }

  function SortableEmptyChip({ id }) {
    const { attributes, listeners, setNodeRef, transform, transition, isOver } = useSortable({ id });
    return (
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        data-testid={`empty-chip-${id}`}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          minWidth: 60,
          minHeight: 28,
          justifyContent: 'center',
          opacity: isOver ? 1 : 0.7,
          background: isOver ? 'rgba(59,130,246,0.12)' : undefined,
        }}
        className="inline-flex items-center px-2 py-1 m-1 rounded border border-dashed border-blue-300 text-blue-400 bg-blue-50/50 text-xs select-none"
      >
        Drop here or <span className="ml-1">+ Add</span>
      </div>
    );
  }

  // Helper to convert a field value to an array of chips (for numbers/strings)
  function toChips(val) {
    if (val === null || val === undefined) return [];
    if (Array.isArray(val)) return [...val];
    if (typeof val === 'string') return val.trim() ? [val] : [];
    if (typeof val === 'number') return [val.toString()];
    return [];
  }

  // Add DroppableField component
  function DroppableField({ id, children }) {
    const { isOver, setNodeRef } = useDroppable({ id });
    return (
      <div
        ref={setNodeRef}
        className={`${isOver ? 'ring-2 ring-blue-400 rounded-lg bg-blue-500/10' : ''} transition-all duration-200`}
      >
        {children}
      </div>
    );
  }

  // Add UploadNextButton component
  const UploadNextButton = () => {
    const remainingImages = MAX_IMAGES - totalProcessedImages;
    const canUploadMore = remainingImages > 0;

    return (
      <div className="flex justify-center w-full">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            setImages([]);
            setImagePreviews(new Map());
            setStep(1);
          }}
          className={`
            ${isMobile ? 'w-3/4' : 'w-11/12 sm:w-3/4'} mb-4 
            ${isMobile ? 'px-4 py-2 text-sm' : 'px-8 py-4'} rounded-xl font-semibold
            transition-all duration-300 flex items-center justify-center gap-2
            bg-gradient-to-r from-blue-600 to-blue-400 text-white hover:shadow-lg
          `}
        >
          {canUploadMore ? `Upload Next (${remainingImages} remaining)` : 'Upload Complete'}
        </motion.button>
      </div>
    );
  };

  // UI
  if (step === 3) {
    return <Results result={result} loading={loading} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        ${isMobile ? 'w-full' : 'mx-auto my-10 p-8'} ${isMobile ? '' : 'rounded-2xl'}
        ${isMobile ? '' : 'border border-blue-800/20'}
        ${isMobile ? '' : 'bg-gradient-to-br from-gray-950/80 to-blue-950/60'}
        ${isMobile ? '' : 'backdrop-blur-xl shadow-2xl'}
        ${isMultiCard ? 'w-full max-w-[90vw]' : 'w-fit'}
      `}
      style={{
        background: isMobile ? 'transparent' : 'linear-gradient(135deg, rgba(30,41,59,0.85) 0%, rgba(30,58,138,0.75) 100%)',
        boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0,0,0,0.25), 0 4px 8px rgba(0,0,0,0.18)',
        borderRadius: isMobile ? '0' : '1.5rem',
        border: isMobile ? 'none' : '1px solid rgba(59,130,246,0.12)',
        backdropFilter: isMobile ? 'none' : 'blur(16px)',
        WebkitBackdropFilter: isMobile ? 'none' : 'blur(16px)',
        overflow: 'hidden',
        width: isMobile ? 'calc(100% + 40px)' : '',
        margin: isMobile ? '0 -20px' : '',
        position: isMobile ? 'relative' : '',
        left: isMobile ? '0' : '',
        transform: isMobile ? 'translateX(-10px)' : '',
      }}
    >
      {step === 1 && (
        <div className="space-y-6">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={`rounded-xl border-2 border-dashed transition-all duration-300 flex flex-col items-center justify-center space-y-4 ${isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-gray-300 hover:border-blue-500'}`}
            style={{
              background: 'rgba(30,41,59,0.5)',
              width: '100%', // Default to full width for mobile
              minWidth: isMobile ? 'auto' : '32rem',
              maxWidth: '90vw',
              height: isMobile ? '18rem' : '24rem',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            {/* Pulsing Banner - Only show on desktop and when no images are uploaded */}
            {!isMobile && images.length === 0 && !processing && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute top-4 left-4 right-4"
            >
              <motion.div
                animate={{
                  boxShadow: [
                    '0 0 15px rgba(59,130,246,0.15)',
                    '0 0 25px rgba(59,130,246,0.25)',
                    '0 0 15px rgba(59,130,246,0.15)'
                  ],
                  scale: [1, 1.01, 1]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="bg-gradient-to-r from-blue-500/80 to-blue-600/80 backdrop-blur-sm rounded-lg py-2 px-4 text-center"
              >
                <p className="text-white text-sm font-medium flex items-center justify-center gap-2">
                  <span className="text-base">💡</span>
                  For best results, upload screenshots [not images] of your species cards
                </p>
              </motion.div>
            </motion.div>
            )}

            {processing ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <p className="mt-4 text-gray-300">Processing image...</p>
              </div>
            ) : images.length > 0 ? (
              <>
                <button
                  onClick={() => {
                    // Clean up blob URLs when removing images
                    imagePreviews.forEach(url => URL.revokeObjectURL(url));
                    setImagePreviews(new Map());
                    setImages([]);
                    setResults([]);
                  }}
                  className="text-sm text-gray-400 hover:text-blue-400 absolute top-2 right-4 z-10"
                >
                  Remove images
                </button>
                <div
                  className="grid gap-4 w-full px-4 mb-4 overflow-y-auto"
                  style={{
                    gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '120px' : '160px'}, 1fr))`,
                    maxHeight: isMobile ? '10rem' : '12rem',
                    marginTop: isMobile ? '2rem' : '2.5rem',
                  }}
                >
                  {images.map((img, i) => (
                    <img
                      key={i}
                      src={imagePreviews.get(img)}
                      alt={`Uploaded ecosystem ${i+1}`}
                      className={`${isMobile ? 'h-24' : 'h-32'} w-full object-cover rounded-lg shadow-md`}
                    />
                  ))}
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => processImages(images)}
                  disabled={processing || images.length === 0}
                  className={`
                    ${isMobile ? 'w-3/4' : 'w-11/12 sm:w-3/4'} mb-4 
                    ${isMobile ? 'px-4 py-2 text-sm' : 'px-8 py-4'} rounded-xl font-semibold
                    transition-all duration-300 flex items-center justify-center gap-2
                    ${processing ? 'bg-gray-400 cursor-not-allowed opacity-70'
                                 : 'bg-gradient-to-r from-blue-600 to-blue-400 text-white hover:shadow-lg'}
                  `}
                  style={{ pointerEvents: processing || images.length === 0 ? 'none' : 'auto' }}
                >
                  {processing ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    'Process Images'
                  )}
                </motion.button>
              </>
            ) : (
              <>
                {isMobile ? (
                  <>
                    {/* Mobile-only upload UI, with no camera emoji and no upload text */}
                    <div className="flex flex-col items-center justify-center w-full my-2 gap-2">
                      <p className="text-sm text-red-400 flex items-center justify-center gap-1">
                        <span>⚠️</span> UP TO 3 IMAGES AT A TIME <span>⚠️</span>
                      </p>
                      <label className="px-4 py-2 text-sm font-medium text-blue-400 border-2 border-blue-400 rounded-lg hover:bg-blue-400/10 cursor-pointer">
                        Choose Files
                        <input type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
                      </label>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Desktop UI remains unchanged */}
                <div className="text-4xl mt-8" style={{ marginTop: '2.5rem' }}>📸</div>
                <p className="text-gray-300">Drag and drop your ecosystem images here</p>
                <p className="text-sm text-red-400 flex items-center justify-center gap-1">
                  <span>⚠️</span> UP TO 3 IMAGES AT A TIME <span>⚠️</span>
                </p>
                <p className="text-sm text-gray-400">or</p>
                <label className="px-4 py-2 text-sm font-medium text-blue-400 border-2 border-blue-400 rounded-lg hover:bg-blue-400/10 cursor-pointer">
                  Choose Files
                  <input type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
                </label>
                  </>
                )}

                {/* Pro Tip */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-6 p-4 rounded-lg bg-gradient-to-r from-blue-500/10 to-blue-600/10 border border-blue-500/20"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl">💡</span>
                    <div>
                      <h4 className="text-blue-200 font-medium mb-1">Pro Tip</h4>
                      <p className="text-sm text-blue-100/90">
                        For best results our engine needs a stable and fast internet connection. Use clear and well-lit screenshots of your species cards.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </div>
          
          {error && (
            <div className="bg-red-500/20 text-red-300 p-4 rounded-lg text-center mt-4">
              {error}
            </div>
          )}
        </div>
      )}

      {step === 2 && results.length > 0 && (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;

            // Parse the active (source) ID
            const activeParts = active.id.split('-');
            const activeField = activeParts[0];
            const activeCard = parseInt(activeParts[1]);
            const activeIdx = parseInt(activeParts[2]);

            // Parse the over (target) ID
            let overParts = over.id.split('-');
            const overField = overParts[0];
            const overCard = parseInt(overParts[1]);
            
            // Handle zone drops
            if (over.id.endsWith('-zone')) {
              overParts[2] = 'empty';
            }
            const overIdx = overParts[2] === 'empty' ? -1 : parseInt(overParts[2]);

            // Create a new copy of results
            const newResults = [...results];

            // Get the source chips array
            const sourceChips = toChips(newResults[activeCard][activeField]);
            if (!sourceChips || activeIdx >= sourceChips.length) return;

            // Remove the chip from source
            const [movedChip] = sourceChips.splice(activeIdx, 1);
            newResults[activeCard][activeField] = sourceChips;

            // Get the target chips array
            const targetChips = toChips(newResults[overCard][overField]);

            // Handle empty field case
            if (overParts[2] === 'empty') {
              targetChips.push(movedChip);
            } else {
              // Insert at specific position for non-empty fields
              targetChips.splice(overIdx, 0, movedChip);
            }

            // Update the target field
            newResults[overCard][overField] = targetChips;

            // Update state
            setResults(newResults);
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`relative ${isMobile ? 'p-3' : 'p-4'} rounded-xl bg-gradient-to-r from-blue-500/10 to-blue-600/10 border border-blue-500/20`}
            >
              {/* Glow effect */}
              <motion.div
                initial={{ opacity: 0.5 }}
                animate={{ 
                  opacity: [0.5, 0.8, 0.5],
                  boxShadow: [
                    '0 0 20px rgba(59,130,246,0.2)',
                    '0 0 30px rgba(59,130,246,0.3)',
                    '0 0 20px rgba(59,130,246,0.2)'
                  ]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 rounded-xl"
              />
              
              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <span className={`${isMobile ? 'text-xl' : 'text-2xl'}`}>👀</span>
                  <h3 className={`text-blue-100 font-semibold ${isMobile ? 'text-lg' : 'text-xl'}`}>Review Your Data</h3>
                </div>
                <p className={`text-blue-200/90 ${isMobile ? 'text-sm' : 'text-base'} leading-relaxed`}>
                  Our processor might not always capture your image perfectly. Please review the extracted information below and make any necessary corrections. You can drag items to reorder them and click the ✏️ icon to edit values. All fields are fully editable for your convenience.
                </p>
              </div>
            </motion.div>

            <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-white ${isMobile ? 'mb-3' : 'mb-4'} flex items-center gap-2`}>
              <span className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} flex items-center justify-center bg-white/10 rounded-lg text-white`}>📝</span>
              Review Extracted Information
            </h2>

                        <div 
              className="grid gap-6" 
              style={{ 
                gridTemplateColumns: isMobile 
                  ? '1fr' // Always single column on mobile
                  : isMultiCard
                    ? 'repeat(2, minmax(28rem, 1fr))'
                    : 'repeat(auto-fit, minmax(28rem, 1fr))'
              }}
            >
              {results.map((result, idx) => {
                const caloriesNeeded = Array.isArray(result.caloriesNeeded) ? result.caloriesNeeded[0] : result.caloriesNeeded;
                const caloriesProvided = Array.isArray(result.caloriesProvided) ? result.caloriesProvided[0] : result.caloriesProvided;
                const isProducer = (!caloriesNeeded || caloriesNeeded === 0 || caloriesNeeded === '0') && caloriesProvided > 0;
                const badge = isProducer ? { emoji: '🌿', label: 'Producer', color: 'bg-green-100 text-green-800' } : { emoji: '🦁', label: 'Animal', color: 'bg-yellow-100 text-yellow-800' };
                return (
                  <div key={idx}
                    className={`relative ${isMobile ? 'p-4' : 'p-6'} bg-gradient-to-br from-gray-950/80 to-blue-950/60 rounded-xl border border-blue-800/20 shadow-xl`}
                    style={{ 
                      maxWidth: isMobile ? '100%' : '40rem', 
                      minWidth: isMobile ? 'auto' : '28rem', 
                      width: '100%', 
                      backdropFilter: 'blur(12px)', 
                      WebkitBackdropFilter: 'blur(12px)', 
                      boxShadow: '0 8px 32px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)' 
                    }}
                  >
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-2 ${badge.color}`} style={{fontWeight: 600}}>
                      <span className="text-lg">{badge.emoji}</span>
                      {badge.label}
                    </div>
                    <div className="font-bold mb-2 text-blue-100">{result.file || `Image ${idx + 1}`}</div>
                    {result.error ? (
                      <div className="text-red-600">Error: {result.error}</div>
                    ) : (
                      <>
                        <div className="mb-2 whitespace-pre-line">
                          <strong className="text-blue-200">Extracted Information:</strong>
                          <div className={`${isMobile ? 'p-3' : 'p-4'} bg-blue-50/80 rounded-lg mt-2 text-blue-900`}
                            style={{ 
                              maxWidth: isMobile ? '100%' : '38rem', 
                              minWidth: isMobile ? 'auto' : '24rem', 
                              width: '100%' 
                            }}
                          >
                            {Object.entries(fieldMap).map(([field, type]) => {
                              const emoji = field === 'name' ? '📝' : field === 'caloriesProvided' ? '🔥' : field === 'caloriesNeeded' ? '⚡' : field === 'temperatureRange' ? '🌡️' : field === 'depthRange' ? '🌊' : field === 'foodSources' ? '🍽️' : field === 'eatenBy' ? '🦁' : '';
                              return (
                                <DroppableField key={field} id={`${field}-${idx}-zone`}>
                                  <div className="mb-2">
                                  <span className={`font-medium capitalize text-blue-100 flex items-center gap-2 ${isMobile ? 'text-sm' : 'text-base'}`}>
                                    {emoji} {field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}:
                                  </span>
                                  <SortableContext items={toChips(result[field]).length === 0 ? [`${field}-${idx}-empty`] : toChips(result[field]).map((_,i)=>`${field}-${idx}-${i}`)} strategy={verticalListSortingStrategy}>
                                    {toChips(result[field]).length === 0 ? (
                                      <SortableEmptyChip id={`${field}-${idx}-empty`} />
                                    ) : (
                                      toChips(result[field]).map((chip, chipIdx) => (
                                        <SortableEditableChip
                                          key={`${field}-${idx}-${chipIdx}`}
                                          id={`${field}-${idx}-${chipIdx}`}
                                          value={chip}
                                          emoji={emoji}
                                          editing={editingChip && editingChip.cardIdx === idx && editingChip.field === field && editingChip.chipIdx === chipIdx}
                                          setEditing={isEditing => {
                                            if (isEditing) setEditingChip({ cardIdx: idx, field, chipIdx });
                                            else setEditingChip(null);
                                          }}
                                          onEdit={v => {
                                            const newResults = [...results];
                                            const chipsArr = toChips(newResults[idx][field]);
                                            chipsArr[chipIdx] = v;
                                            newResults[idx][field] = chipsArr;
                                            setResults(newResults);
                                          }}
                                        />
                                      ))
                                    )}
                                  </SortableContext>
                                  <button className="ml-2 text-xs text-blue-500 hover:text-blue-700" onClick={() => {
                                    const newResults = [...results];
                                    const chipsArr = toChips(newResults[idx][field]);
                                    chipsArr.push('');
                                    newResults[idx][field] = chipsArr;
                                    setResults(newResults);
                                    setEditingChip({ cardIdx: idx, field, chipIdx: chipsArr.length - 1 });
                                  }}>+ Add</button>
                                </div>
                                </DroppableField>
                              );
                            })}
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {totalProcessedImages < MAX_IMAGES ? (
              <UploadNextButton />
            ) : (
            <SolveEcosystemButton
              onSolve={async () => {
                setSolverError('');
                if (!results.length) return;
                // Parse depth/temp from first result
                const first = results[0];
                const parseRange = (range, type) => {
                  if (!range) return [null, null];
                  // Remove units and split on -
                  let cleaned = range.replace(/[^0-9\-.]/g, '-');
                  let [min, max] = cleaned.split('-').map(Number).filter(x => !isNaN(x));
                  if (typeof min === 'undefined') min = null;
                  if (typeof max === 'undefined') max = min;
                  return [min, max];
                };
                const [minDepth, maxDepth] = parseRange(Array.isArray(first.depthRange) ? first.depthRange[0] : first.depthRange, 'depth');
                const [minTemp, maxTemp] = parseRange(Array.isArray(first.temperatureRange) ? first.temperatureRange[0] : first.temperatureRange, 'temp');
                // Check all results for matching depth/temp
                let mismatch = false;
                for (const r of results) {
                  const [d1, d2] = parseRange(Array.isArray(r.depthRange) ? r.depthRange[0] : r.depthRange, 'depth');
                  const [t1, t2] = parseRange(Array.isArray(r.temperatureRange) ? r.temperatureRange[0] : r.temperatureRange, 'temp');
                  if (d1 !== minDepth || d2 !== maxDepth || t1 !== minTemp || t2 !== maxTemp) {
                    mismatch = true;
                    break;
                  }
                }
                if (mismatch) {
                  setSolverError('Depth/temperature ranges do not align across all images. Please ensure all cards have the same depth and temperature range.');
                  return;
                }
                // Split producers/animals
                const producers = results.filter(r => {
                  const caloriesProvided = Array.isArray(r.caloriesProvided) ? r.caloriesProvided[0] : r.caloriesProvided;
                  const caloriesNeeded = Array.isArray(r.caloriesNeeded) ? r.caloriesNeeded[0] : r.caloriesNeeded;
                  const hasCaloriesNeeded = caloriesNeeded !== undefined && caloriesNeeded !== null && caloriesNeeded !== '' && caloriesNeeded !== 0 && caloriesNeeded !== '0';
                  const hasCaloriesProvided = caloriesProvided !== undefined && caloriesProvided !== null && caloriesProvided !== '' && caloriesProvided !== 0 && caloriesProvided !== '0';
                  return !hasCaloriesNeeded && hasCaloriesProvided;
                }).map(p => ({
                  name: Array.isArray(p.name) ? p.name[0] : p.name,
                  calories: Array.isArray(p.caloriesProvided) ? Number(p.caloriesProvided[0]) : Number(p.caloriesProvided),
                  eatenBy: Array.isArray(p.eatenBy) ? p.eatenBy : [],
                }));
                
                const animals = results.filter(r => {
                  const caloriesNeeded = Array.isArray(r.caloriesNeeded) ? r.caloriesNeeded[0] : r.caloriesNeeded;
                  return caloriesNeeded !== undefined && caloriesNeeded !== null && caloriesNeeded !== '' && caloriesNeeded !== 0 && caloriesNeeded !== '0';
                }).map(a => ({
                  name: Array.isArray(a.name) ? a.name[0] : a.name,
                  caloriesNeeded: Array.isArray(a.caloriesNeeded) ? Number(a.caloriesNeeded[0]) : Number(a.caloriesNeeded),
                  caloriesProvided: Array.isArray(a.caloriesProvided) ? Number(a.caloriesProvided[0]) : Number(a.caloriesProvided),
                  foodSources: Array.isArray(a.foodSources) ? a.foodSources : [],
                }));
                
                // Prepare solver input
                const ecosystemName = first.name || (animals[0]?.name || producers[0]?.name || 'Unknown');
                const solverInput = {
                  animals,
                  producers,
                  depthTemperature: {
                    minDepth,
                    maxDepth,
                    minTemp,
                    maxTemp
                  },
                  ecosystemName,
                };
                
                await solve(solverInput);
                setStep(3);
              }}
              isLoading={loading}
              progress={loading ? 50 : 0}
            />
            )}
            {solverError && (
              <div className="mt-4 p-4 rounded-lg bg-red-900/20 text-red-300 text-center">
                {solverError}
              </div>
            )}
          </motion.div>
        </DndContext>
      )}

      {error && !step === 1 && (
        <div className="mt-2 text-red-400 text-sm font-semibold">{error}</div>
      )}
    </motion.div>
  );
}

export default OcrUpload; 
